import { BehaviorSubject, Observable, OperatorFunction } from 'rxjs';
import { take } from 'rxjs/operators';

export class AsyncHlp {
  /**
   * Returns promise completed after given amount of milliseconds
   * @param ms number of milliseconds to wait
   */
  public static async wait(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Executes given code as new microtask to prevent Angular "NG0100 Expression Changed After Checked" error
   * @param {function} resolver
   * @return {Promise<T>}
   */
  public static inNextTick<T>(resolver: () => T): Promise<T> {
    return Promise.resolve().then(resolver);
  }

  /**
   * Update BehaviorSubject data or create and assign if not exist
   * @param {BehaviorSubject<T> | null} bs   BehaviorSubject object
   * @param {T}                         data to assign or update into bs
   */
  public static updateBS<T>(bs: BehaviorSubject<T> | null, data: T): BehaviorSubject<T> {
    if (!bs) bs = new BehaviorSubject<T>(data);
    else bs.next(data);
    return bs;
  }

  /**
   * Captures first value emitted as observable, and returns it as promise
   * @param observable
   */
  public static async onFirst<T>(observable: Observable<T>): Promise<T>;
  /**
   * Captures first value emitted as observable processed by operator, and returns it as promise
   * @param observable
   * @param o1         operator function
   */
  public static async onFirst<T, A>(observable: Observable<T>, o1: OperatorFunction<T, A>): Promise<A>;
  /**
   * Captures first value emitted as observable processed by operators, and returns it as promise
   * @param observable
   * @param o1         operator function
   * @param o2         operator function
   */
  // eslint-disable-next-line max-len
  public static async onFirst<T, A, B>(observable: Observable<T>, o1: OperatorFunction<T, A>, o2: OperatorFunction<A, B>): Promise<B>; // prettier-ignore
  /**
   * Captures first value emitted as observable processed by operators, and returns it as promise
   * @param observable
   * @param o1         operator function
   * @param o2         operator function
   * @param o3         operator function
   */
  // eslint-disable-next-line max-len
  public static async onFirst<T, A, B, C>(observable: Observable<T>, o1: OperatorFunction<T, A>, o2: OperatorFunction<A, B>, o3: OperatorFunction<B, C>): Promise<C>; // prettier-ignore
  /**
   * Captures first value emitted as observable processed by operators, and returns it as promise
   * @param observable
   * @param o1         operator function
   * @param o2         operator function
   * @param o3         operator function
   * @param operators  more operator functions (without type safety - if you need to call this, consider adding more
   *                   strong-typed headers)
   */
  // eslint-disable-next-line max-len
  public static async onFirst<T, A, B, C>(observable: Observable<T>, o1: OperatorFunction<T, A>, o2: OperatorFunction<A, B>, o3: OperatorFunction<B, C>, ...operators: OperatorFunction<any, any>[]): Promise<unknown>; // prettier-ignore
  // implementation
  public static async onFirst<T>(observable: Observable<T>, ...operators: OperatorFunction<any, any>[]): Promise<unknown> {
    let _observable: Observable<any> = observable;
    operators.forEach((operator) => (_observable = _observable.pipe(operator)));
    return new Promise((resolve, reject) => {
      let hasValue = false;

      _observable.pipe(take(1)).subscribe({
        next: (value) => {
          hasValue = true;
          resolve(value);
        },
        error: reject,
        complete: () => {
          if (!hasValue) resolve(undefined);
        }
      });
    });
  }
}
